// shared-library/api/services/auth.js
/**
 * 认证相关API服务
 */
export default function(apiClient) {
  const encryptPassword = async (user_password) => {
    const response = await apiClient.post('/auth/encrypt_password', { user_password: user_password });
    return response.data.encrypted_password;
  };

  return {
    /**
     * 用户登录
     * @param {Object} credentials - 登录凭证
     * @param {string} credentials.email - 用户邮箱
     * @param {string} credentials.password - 用户密码
     * @param {boolean} credentials.remember - 是否记住登录状态
     * @returns {Promise} - 返回包含token和用户信息的Promise
     */
    async login(credentials) {
      const encryptedPassword = await encryptPassword(credentials.user_password);
      return apiClient.post('/auth/login', {
        ...credentials,
        user_password: encryptedPassword
      });
    },

    // ✅ 新增：刷新 token
    /**
     * 刷新访问令牌
     * @returns {Promise} - 返回包含新 access_token 的 Promise
     */
    refreshToken() {
      return apiClient.post('/auth/token/refresh');
    },

    /**
     * 修改密码
     * @param {Object} passwordData - 密码数据
     * @param {string} passwordData.currentPassword - 当前密码
     * @param {string} passwordData.newPassword - 新密码
     * @returns {Promise} - 返回修改结果的 Promise
     */
    async changePassword(passwordData) {
      // 先加密当前密码和新密码
      const encryptedCurrentPassword = await encryptPassword(passwordData.currentPassword)
      const encryptedNewPassword = await encryptPassword(passwordData.newPassword)
      
      return apiClient.post('/auth/password', {
        current_password: encryptedCurrentPassword,
        new_password: encryptedNewPassword
      })
    },

    /**
     * 微信登录
     * @param {Object} data - 微信登录数据
     * @param {string} data.code - 微信授权码
     * @param {string} data.state - 微信状态参数
     * @returns {Promise} - 返回包含token和用户信息的Promise
     */
    wechatLogin(data) {
      return apiClient.post('/auth/wechat/own/login', data);
    },

    /**
     * 微信第三方应用登录
     * @param {Object} data - 微信登录数据
     * @param {string} data.code - 微信授权码
     * @param {string} data.state - 微信状态参数
     * @returns {Promise} - 返回包含token和用户信息的Promise
     */
    wechatLoginThird(data) {
      return apiClient.post('/auth/wechat/third/login', data);
    },

    /**
     * 微信代开发应用登录
     * @param {Object} data - 微信登录数据
     * @param {string} data.code - 微信授权码
     * @param {string} data.state - 微信状态参数
     * @returns {Promise} - 返回包含token和用户信息的Promise
     */
    wechatLoginAgent(data) {
      return apiClient.post('/auth/wechat/agent/login', data);
    },
    
    /**
     * 网页客户端微信登录
     * @param {Object} data - 微信登录数据
     * @param {string} data.code - 微信授权码
     * @returns {Promise} - 返回包含token和用户信息的Promise
     */
    wechatClientLogin(data) {
      return apiClient.post('/auth/wechat/own/login/client', data);
    },
    
    /**
     * 用户注册
     * @param {Object} userData - 用户数据
     * @param {string} userData.name - 用户名
     * @param {string} userData.email - 用户邮箱
     * @param {string} userData.password - 用户密码
     * @returns {Promise} - 返回包含token和用户信息的Promise
     */
    async register(userData) {
      const encryptedPassword = await encryptPassword(userData.user_password);
      return apiClient.post('/auth/register', {
        ...userData,
        user_password: encryptedPassword
      });
    },
    
    /**
     * 用户登出
     * @returns {Promise} - 返回登出结果的Promise
     */
    logout() {
      return apiClient.post('/auth/logout');
    },
    
    /**
     * 获取当前登录用户信息
     * @returns {Promise} - 返回用户信息的Promise
     */
    getCurrentUser() {
      return apiClient.get('/auth/me');
    },
    
    /**
     * 获取当前用户菜单（仅管理员端需要）
     * @returns {Promise} - 返回用户菜单的Promise
     */
    getUserMenus() {
      return apiClient.get('/auth/menu');
    },
    
    /**
     * 获取当前用户的数据中台菜单（仅企业微信数据中台需要）
     * @returns {Promise} - 返回用户菜单的Promise
     */
    getDHDataUserMenus() {
      return apiClient.get('/auth/menu/dhdata');
    },
    
    /**
     * 更新用户个人资料
     * @param {Object} userData - 用户资料数据
     * @returns {Promise} - 返回更新后的用户信息的Promise
     */
    updateProfile(userData) {
      return apiClient.put('/auth/profile', userData);
    },
    
    /**
     * 请求重置密码
     * @param {Object} data - 包含邮箱的对象
     * @param {string} data.email - 用户邮箱
     * @returns {Promise} - 返回请求结果的Promise
     */
    requestPasswordReset(data) {
      return apiClient.post('/auth/password/reset-request', data);
    },
    
    /**
     * 重置密码
     * @param {Object} resetData - 重置数据
     * @param {string} resetData.token - 重置令牌
     * @param {string} resetData.password - 新密码
     * @returns {Promise} - 返回重置结果的Promise
     */
    resetPassword(resetData) {
      return apiClient.post('/auth/password/reset', resetData);
    },
    
    /**
     * 验证邮箱
     * @param {string} token - 验证令牌
     * @returns {Promise} - 返回验证结果的Promise
     */
    verifyEmail(token) {
      return apiClient.post(`/auth/email/verify/${token}`);
    },
    
    /**
     * 重新发送验证邮件
     * @returns {Promise} - 返回发送结果的Promise
     */
    resendVerificationEmail() {
      return apiClient.post('/auth/email/resend');
    },
    
    /**
     * 获取用户偏好设置
     * @returns {Promise} - 返回用户偏好设置的Promise
     */
    getUserPreferences() {
      return apiClient.get('/auth/preferences');
    },
    
    /**
     * 更新用户偏好设置
     * @param {string} key - 偏好键名
     * @param {any} value - 偏好值
     * @returns {Promise} - 返回更新结果的Promise
     */
    updateUserPreference(key, value) {
      return apiClient.post(`/auth/preferences/${key}`, { value: value });
    },
    
    /**
     * 获取所有用户列表（管理员功能）
     * @returns {Promise}
     */
    getAllUsers() {
      return apiClient.get('/auth/admin/users');
    },
    
    /**
     * 重置用户密码为 admin1234（管理员功能）
     * @param {number} userId - 用户ID
     * @returns {Promise}
     */
    resetUserPassword(userId) {
      return apiClient.post(`/auth/admin/users/${userId}/reset-password`);
    }
  };
}